import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useApp } from '../store';
import { stillBlocked } from '../blocked';
import { percentDone } from '../progress';
import type { Issue } from '../types';
import { inFlight } from '../insights';
import { epicCompletionSeries } from '../epic-rollup';
import Spark from './portfolio/Spark';
import InsightsStrip from './InsightsStrip';
import InsightsGrid from './InsightsGrid';
import DataQualityPanel from './DataQualityPanel';
import {
  CumulativeFlow, CycleTime, Forecast, InFlight, InYourWay, KeepingUp, LeadTime, PushedDates, Throughput, WhereTheWork, useInsightsData,
} from './InsightPanels';
import {
  DEFAULT_LAYOUT, readLayout, writeLayout, type Layout, type PanelId,
} from '../insights-layout';

const isClosed = (i: Issue) => i.status === 'done' || i.status === 'wontdo';


export default function Dashboard() {
  // Scoped to the strip's choice — this board or all of them — so the glance line
  // and every panel below it count the same work. See useInsightsData.
  const { issues } = useInsightsData();
  const profiles = useApp((s) => s.profiles);
  // An admin can switch the Data quality panel off for this board (configureAssists,
  // 0205); absent reads as on. The panel is board-scoped, so it reads the current
  // board's flag even when Insights is showing all boards.
  const dataQualityOn = useApp((s) => s.meta.dataQualityPanel !== false);
  // The current board's delivery mode — unset reads as a project (see
  // ProjectMeta.deliveryMode). It keys the completion % only: a continuous
  // board's backlog is a candidate pool, not planned scope.
  const deliveryMode = useApp((s) => s.meta.deliveryMode);

  /*
   * ALL-BOARDS NEEDS THE ROLL-UP LOADED, EVEN ON A COLD OPEN — the interaction
   * and workflow witnesses caught this together (24 Sep 2026): the scope is
   * restored from localStorage directly, bypassing setInsightsScope, so opening
   * Insights straight onto "all my boards" after a reload read an empty roll-up
   * and drew a confident, wrong screen of zeroes. `setInsightsScope` triggers the
   * load when you click the tab; this triggers the same load when the screen
   * mounts already in that scope. Guarded by the empty check, so it never
   * double-loads or refires while a load is in flight.
   */
  const scope = useApp((s) => s.insightsScope);
  const portfolioEmpty = useApp((s) => s.portfolioIssues.size === 0);
  const portfolioLoading = useApp((s) => s.portfolioLoading);
  const loadPortfolio = useApp((s) => s.loadPortfolio);
  useEffect(() => {
    if (scope === 'all' && portfolioEmpty && !portfolioLoading) void loadPortfolio();
  }, [scope, portfolioEmpty, portfolioLoading, loadPortfolio]);

  // Open = committed work still on the board. The BACKLOG is candidate work — the
  // holding pen — so it is never "open" (the aggregate-bucket invariant: backlog
  // is candidate, never committed). True in every mode and scope.
  const open = issues.filter((i) => !isClosed(i) && i.status !== 'backlog');
  // The same rule the board uses — a blocker whose issue is finished is not one.
  const blocked = open.filter((i) => stillBlocked(i, open));
  // Completion %: mode-keyed (Vinnie, 30 Oct). A CONTINUOUS board's backlog is a
  // candidate pool, so it leaves the denominator; a PROJECT's backlog is planned
  // work still to do, so it stays — matching the Forecast Finish scope so the two
  // never disagree. All-boards is a blend of modes, so it keeps the default
  // (project) rule and leaves backlog in. Only a single continuous board strips it.
  const continuousBoard = scope !== 'all' && deliveryMode === 'continuous';
  const pct = percentDone(continuousBoard ? issues.filter((i) => i.status !== 'backlog') : issues);
  // The two aging numbers the glance line shows come from the same reading as the
  // aging hero, so the strip and the panel can never disagree about what is stuck.
  const flight = inFlight(issues, Date.now(), profiles);
  const glance = { open: open.length, pct, inFlight: flight.total, stuck: flight.stalled, blocked: blocked.length };

  /*
   * READ ONCE, not on every render. `readLayout` parses and repairs a stored
   * value; doing that in the render body would rebuild the arrays each pass.
   */
  const [layout, setLayoutState] = useState<Layout>(() => (typeof localStorage === 'undefined' ? DEFAULT_LAYOUT : readLayout()));
  const setLayout = (next: Layout) => {
    setLayoutState(next);
    writeLayout(next);
  };

  // Arrange MODE — on the whole screen while it is on: every card shows its grip,
  // pin and resize handle, and the "Show panels" checklist opens. Lifted here (not
  // in the strip) because the grid below needs the same flag. The grid is excluded
  // from the strip's click-away, so grabbing a card does not close arranging.
  const [arranging, setArranging] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);

  /*
   * THE PANELS, KEYED BY ID — the same components the screen has always drawn,
   * handed to the grid to place, size, reorder and pin. Written in one place so a
   * change to one panel is a change to one panel; the grid never edits them.
   */
  const nodeFor: Record<PanelId, ReactNode> = {
    'in-flight': <InFlight />,
    'throughput': <Throughput />,
    'cycle-time': <CycleTime />,
    'lead-time': <LeadTime />,
    'keeping-up': <KeepingUp />,
    'cumulative-flow': <CumulativeFlow />,
    'in-your-way': <InYourWay />,
    'pushed-dates': <PushedDates />,
    'where-the-work': <WhereTheWork />,
    'by-epic': <ByEpicPanel issues={issues} />,
    'forecast': <Forecast />,
  };

  return (
    <div className="dashboard">
      {/* THE STRIP replaces the four tiles: the same facts as a glance line, beside
          the controls that decide what they count — this board or all of them, and
          how far back — plus Arrange. #303 follow-up. */}
      <InsightsStrip
        glance={glance}
        layout={layout}
        onLayoutChange={setLayout}
        arranging={arranging}
        onArrangingChange={setArranging}
      />

      {/* DATA QUALITY — the gaps the board's own numbers lean on, gathered for this
          board with one action per row. Above the grid because it makes the panels
          below it trustworthy; quiet one-liner when the board is clean. Off when an
          admin has turned the panel off for this board (0205). */}
      {dataQualityOn && <DataQualityPanel />}

      {/* THE UNIFORM SNAP GRID — every panel one card on a 12-column grid, dragged,
          resized and pinned in place. Style B, approved #269. */}
      <InsightsGrid
        layout={layout}
        arranging={arranging}
        onChange={setLayout}
        nodeFor={nodeFor}
        gridRef={gridRef}
      />
    </div>
  );
}

/**
 * EPIC COMPLETION OVER TIME — a MEASURE, not an action view. One row per epic:
 * its size-weighted completion now, an 8-week trend sparkline, and the movement
 * over that window. The acting — what to start, what is blocked — lives on the
 * Epics screen and the Epic detail; Insights only measures, so an epic that is
 * moving reads apart from one that has stalled without ever queueing a card here.
 * (It used to carry that action view — "could start now / waiting on" — which was
 * a third dialect of the epic read on a measurement surface; retired on P2.)
 * Off by default; turned on in "Show panels".
 */
function ByEpicPanel({ issues }: { issues: Issue[] }) {
  const now = Date.now();

  const epics = [...new Set(issues.map((i) => i.epic || 'Unsorted'))]
    .map((name) => {
      const all = issues.filter((i) => (i.epic || 'Unsorted') === name);
      const series = epicCompletionSeries(all, now, 8);
      const pct = series[series.length - 1];
      return { name, total: all.length, series, pct, delta: pct - series[0] };
    })
    // Least complete first — "where is the most work still to do" — then the
    // bigger epic breaks a tie, so the order is stable run to run.
    .sort((a, b) => a.pct - b.pct || b.total - a.total);

  return (
    <section className="panel">
      <h2>Epic Completion over Time</h2>
      <p className="hint" style={{ margin: '0 0 10px' }}>
        Size-weighted, over the last 8 weeks — which epics are moving and which have stalled. The work to act
        on lives in the Portfolio’s Epics lens.
      </p>
      <div className="epic-list">
        {epics.map((e) => {
          const dir = e.delta > 1 ? 'up' : e.delta < -1 ? 'down' : 'flat';
          const move = dir === 'flat' ? 'steady' : `${e.delta > 0 ? '+' : ''}${e.delta} pts · 8 wks`;
          return (
            <div key={e.name} className="epic-trend">
              <div className="epic-trend-main">
                <span className="epic-line-name">{e.name}</span>
                <span className="epic-trend-sub">{e.total} {e.total === 1 ? 'card' : 'cards'}</span>
              </div>
              <div className="epic-trend-mv">
                <Spark values={e.series} />
                <span className="epic-trend-now">{e.pct}%</span>
                <span className={`epic-trend-move ${dir}`}>{move}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
